<script lang="ts" setup>
const {
  text,
  exposure = [-20, -100],
  animated,
} = defineProps<{
  text: string
  exposure?: number | [number, number]
  animated?: boolean
}>()
const words = text
  .split(/\s+/)
  .map((word) => (word.includes('-') ? word.split(/(?<=-)/) : [word]))
  .flat()
const letterRefs = ref<HTMLElement[][]>([])
const letters = computed(() => {
  return text.split('')
})

function lerp(start: number, end: number, amt: number) {
  return (1 - amt) * start + amt * end
}

const target = useTemplateRef<HTMLDivElement>('target')
const targetIsVisible = shallowRef(false)
const { y: scrollY } = useElementBounding(target)

const { stop } = useIntersectionObserver(target, ([entry], _observerElement) => {
  targetIsVisible.value = entry?.isIntersecting || false
})

function getExposure(idx: number) {
  if (!Array.isArray(exposure)) return
  return animated
    ? lerp(
        exposure[0],
        exposure[1],
        targetIsVisible.value
          ? -scrollY.value / window.innerHeight + idx / letters.value.length
          : 0,
      )
    : lerp(exposure[0], exposure[1], idx / letters.value.length)
}
const hasAnimated = ref(false)
const isAnimating = ref(false)

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !hasAnimated.value) {
        hasAnimated.value = true
        target.value?.classList.add('animate-expo-bounce')
      }
    },
    { threshold: 0.5 },
  )

  if (target.value) {
    observer.observe(target.value)
  }

  if (target.value) {
    target.value.addEventListener('animationstart', () => {
      isAnimating.value = true
    })
    target.value.addEventListener('animationend', () => {
      isAnimating.value = false
    })
  }
})
</script>

<template>
  <div ref="target" class="exposed-text">
    <span class="inline">
      <span
        v-for="(word, wordIndex) in words"
        :key="wordIndex"
        class="inline-block break-words"
        aria-hidden="true"
      >
        <span
          v-for="(letter, letterIndex) in word.split('')"
          :key="letterIndex"
          :ref="
            (el) => {
              if (!letterRefs[wordIndex]) {
                letterRefs[wordIndex] = []
              }
              letterRefs[wordIndex][letterIndex] = el
            }
          "
          class="letter inline-block"
          :style="{
            '--exposure': getExposure(letterIndex + words.slice(0, wordIndex).join('').length),
            '--delay': `${(letterIndex + words.slice(0, wordIndex).join('').length) * 100}ms`,
            '--min-expo': exposure[0],
            '--max-expo': exposure[1],
          }"
        >
          {{ letter }}
        </span>

        <span v-if="wordIndex < words.length - 1" class="inline-block">&nbsp;</span>
      </span>
      <span class="sr-only break-words">{{ text }}</span>
    </span>
  </div>
</template>

<style lang="postcss" scoped>
span {
  --exposure: 0;
  font-variation-settings: 'EXPO' var(--exposure);
}

@keyframes expo-bounce {
  from {
    font-variation-settings: 'EXPO' var(--min-expo);
  }
  50% {
    font-variation-settings: 'EXPO' var(--max-expo);
  }
  to {
    font-variation-settings: 'EXPO' var(--exposure);
  }
}

.exposed-text .letter {
  animation: expo-bounce 1.5s ease-in-out alternate var(--delay);
}
</style>
